import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Container, PageTitle, NbCard, NbButton, Chip } from "@/components/nb";
import { toast } from "sonner";
import { Play, X } from "lucide-react";
import { Stagger, Item } from "@/components/motion";

export default function SubmitIdea() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "tech" });
  const [loading, setLoading] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [saved, setSaved] = useState(false);
  const [openVideo, setOpenVideo] = useState(false);

  useEffect(() => { api.get("/ideas/categories").then((r) => { setCats(r.data); setForm((f) => ({ ...f, category: r.data[0]?.id || "tech" })); }); }, []);

  const activeCat = cats.find((c) => c.id === form.category);

  const generate = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error("Fill title & description");
    setLoading(true);
    try {
      const r = await api.post("/ideas/roadmap", form);
      setRoadmap(r.data);
      setSaved(false);
    } catch (err) { toast.error("AI is napping. Try again."); }
    finally { setLoading(false); }
  };

  const save = async () => {
    await api.post("/ideas", form);
    setSaved(true);
    toast.success("Idea posted to the community!");
  };

  return (
    <Container>
      <PageTitle eyebrow="SUBMIT AN IDEA" title="Pitch it. Get a roadmap." sub="AI drafts a 6-step plan. Plus a founder video for your category." />
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <NbCard className="md:col-span-2 !p-6" data-testid="idea-form">
          <label className="font-mono-sl text-xs uppercase">Title</label>
          <input data-testid="idea-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Homework buddy AI" className="w-full mt-1 px-4 py-3 nb-border rounded-lg" />
          <label className="font-mono-sl text-xs uppercase mt-4 block">Category</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {cats.map((c) => (
              <button key={c.id} data-testid={`cat-${c.id}`} onClick={() => setForm({ ...form, category: c.id })} className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${form.category === c.id ? "bg-sl-primary nb-shadow" : "bg-white"}`}>{c.title}</button>
            ))}
          </div>
          <label className="font-mono-sl text-xs uppercase mt-4 block">Description</label>
          <textarea data-testid="idea-desc" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What problem does it solve? Who is it for?" className="w-full mt-1 px-4 py-3 nb-border rounded-lg" />
          <div className="mt-4 flex gap-3 flex-wrap">
            <NbButton onClick={generate} color="black" disabled={loading} data-testid="gen-roadmap">{loading ? "Thinking..." : "Generate roadmap"}</NbButton>
            {roadmap && !saved && <NbButton onClick={save} color="primary" data-testid="post-idea">Post to community</NbButton>}
            {saved && <Chip color="primary">POSTED ✓</Chip>}
          </div>
        </NbCard>

        <div className="md:col-span-3">
          {activeCat?.video && (
            <button onClick={() => setOpenVideo(true)} data-testid="idea-video" className="w-full text-left mb-4">
              <NbCard color="accent" className="!p-4 flex items-center gap-3 nb-hover">
                <div className="w-12 h-12 bg-white nb-border rounded-lg grid place-items-center shrink-0"><Play className="w-6 h-6" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-sl text-[10px] uppercase">GUIDANCE · {activeCat.title.toUpperCase()}</div>
                  <div className="font-heading font-bold truncate">{activeCat.video.title}</div>
                  <div className="text-xs text-neutral-700">by {activeCat.video.teacher}</div>
                </div>
                <Chip color="tertiary">WATCH</Chip>
              </NbCard>
            </button>
          )}
          <h2 className="font-heading font-black text-2xl tracking-tight mb-3">Idea → Reality Roadmap</h2>
          {!roadmap && <NbCard className="text-neutral-500">Your 6-step plan will appear here after you generate it.</NbCard>}
          {roadmap && (
            <Stagger className="space-y-3" data-testid="roadmap-steps">
              {roadmap.steps.map((s, i) => (
                <Item key={i}>
                  <NbCard color={["primary", "secondary", "tertiary", "accent", "primary", "secondary"][i % 6]} className="!p-4">
                    <div className="flex gap-3 items-start">
                      <span className="font-heading font-black text-2xl w-9 h-9 grid place-items-center bg-white nb-border rounded-full shrink-0">{i + 1}</span>
                      <div>
                        <div className="font-heading font-black text-lg">{s.title}</div>
                        <div className="text-neutral-800 mt-1">{s.action}</div>
                      </div>
                    </div>
                  </NbCard>
                </Item>
              ))}
            </Stagger>
          )}
        </div>
      </div>

      {openVideo && activeCat?.video && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-5" onClick={() => setOpenVideo(false)} data-testid="idea-video-modal">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-4xl bg-white nb-border nb-shadow-xl rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-black flex justify-between items-center">
              <div className="font-heading font-bold truncate">{activeCat.video.title}</div>
              <button onClick={() => setOpenVideo(false)} data-testid="close-idea-video" className="p-2 nb-border rounded-lg bg-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="aspect-video bg-black">
              <iframe title={activeCat.video.title} className="w-full h-full" src={`https://www.youtube.com/embed/${activeCat.video.id}?rel=0&autoplay=1`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </motion.div>
        </div>
      )}
    </Container>
  );
}
